/**
 * SpreadsTotals — moneyline-style table view for spread + total markets.
 * One row per canonical game expanded to 4 perspective rows (away spread,
 * home spread, over total, under total). See SpreadsTotalsTable for the
 * column structure and ladder convention.
 *
 * Owns:
 *   - Per-event selected line identities for spread + total chip strips
 *   - Which single row (if any) is currently expanded as a ladder, across
 *     all events — one nullable {eventKey, rowKind}, so "two events open
 *     at once" is unrepresentable rather than merely discouraged (#219)
 *   - Orderbook subscription needs (only the selected line of the expanded
 *     row per event, so we don't subscribe every line on every game)
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { useAccountStore } from '@/stores/accountStore';
import { useAuthStore } from '@/stores/authStore';
import { readSpreadTotalLines, rememberedSpreadTotalLine, rememberSpreadTotalLine, spreadTotalLineScope } from '@/stores/spreadTotalLineMemory';
import { rememberSpreadTotalDefault, spreadTotalDefault } from '@/stores/spreadTotalLineDefaults';
import { boardFeedOwnerRole, setBoardFullLadderTickers, VALUE_DASHBOARD_FEED_SOURCE } from '@/hooks/useBoardFeedNeeds';
import { elapsedSince, localClockStamp, type LocalClockStamp } from '@/lib/relayClock';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { toHydraGameKeyLive } from '@/services/analyticsGameKey';
import type {
  GameEventRow,
  GameRow,
  Sport,
  StatusFilter,
  DateFilter,
  SlateRange,
  MarketSide,
  PriceMode,
  SpreadLine,
  TotalLine,
  TradeTicketContext,
} from '../ValueDashboard/types';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';
import {
  lt2CardId,
  LT2_DASH_CARD_SCOPE,
  SpreadsTotalsTable,
  type ExpandedRow,
  type PillTradeMeta,
  type RowKind,
} from './SpreadsTotalsTable';
import {
  venueAwareLineWindow,
  spreadLineKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
} from './synthesize';
import type { CfbBucket } from '@/lib/cfb/subdivision';
import { cfbRowMatchesBucket } from '@/lib/cfb/subdivision';
import { useDisplaySettings } from '@/stores/displaySettings';
import { pickUpcomingSlate, slateScopeCopy, upcomingSlateCopy } from '../ValueDashboard/upcomingSlate';
import { slateWindowContains, startOfIsoDayMs } from '../ValueDashboard/weeklySlate';
import { executableEvents, useHidePolyOnlyLines } from './polyOnlyLines';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import { buildBetLineContext } from './buildBetLineContext';
import { findSpreadTotalFocusMatch } from '../GameDetail/SpreadTotalSection';
import { focusEventIdentityMatches } from '@/lib/routing/focusedDashboard';
import type { ExternalFocusResolution } from '../ValueDashboard/useFocusedDashboardHydration';
import { initialLineStep, type InitialLineStep } from './initialLine';
import { storedSpreadTotalLineIndex } from './storedLine';
import { defaultLineIndex, nearestToFiftyIndex } from './lineDefault';
import { INITIAL_LINE_WAIT_MS, initialLineVolumeEnabled } from './initialLineConfig';
import { initialLineDecided, noteInitialLineReused, recordInitialLine } from '@/stores/initialLineTally';

interface SpreadsTotalsProps {
  sport: Sport;
  /**
   * #754 — render THESE events instead of reading the sport's own.
   *
   * A discovery search or a Today/This week mode selects whole events across
   * every sport, over a window the board itself does not hold; the dashboard
   * hands the chosen page down rather than teaching this board to span sports.
   * Everything below the rows is unchanged, which is the point: line seeding,
   * orderbook needs, the game-state poll, focus matching, the pill-trade path
   * and the synthesized ticket all run over one set of rows, exactly as they
   * do on the ordinary board.
   *
   * `sport`, the date props and `cfbBucket` still arrive and still describe the
   * SAVED board — they are what the trader comes back to — so they must not
   * narrow a selection made across sports. The #757 fall-forward is suppressed
   * for the same reason: the window is the search's, and standing aside to a
   * different day would answer a question the trader did not ask.
   */
  events?: readonly GameEventRow[] | null;
  /** #653 — narrow a CFB board to one subdivision bucket. The dashboard fetches
   *  moneyline rows and filters them by board key upstream, but this board owns
   *  its own `getGameEvents` read, so the bucket has to arrive as a prop or the
   *  chips would look active here while doing nothing. Absent (every non-CFB
   *  board, and CFB's own "All CFB") means show the whole sport. */
  cfbBucket?: CfbBucket | null;
  /** #1130 — the CFB "Saved only" view, when it is on for this board. Absent
   *  shows the whole slate. Ignored under discovery (`events`), as the
   *  moneyline results ignore it. Must be referentially stable across the
   *  parent's renders: a fresh object re-filters `rows`, and the notify
   *  effect below re-renders the parent, so an inline literal loops. */
  savedOnly?: SpreadsTotalsSavedOnly;
  /** The board's own name when a bucket narrows it ("CFB · FBS"), for the
   *  table title. Absent keeps the title deriving from the rows' sport. */
  boardLabel?: string;
  /** #754 — the whole table title, for discovery results that span sports and
   *  so cannot be described by any one row's sport. See the table's own prop. */
  titleLabel?: string;
  /** The feed has not answered for this selection yet (the dashboard's
   *  `useFeedSettling`): an empty board says "Loading", not "No …". */
  loading?: boolean;
  statusFilter: StatusFilter;
  dateFilter: DateFilter;
  /** First and last day of the board's bounded view, both inclusive. Omitting
   *  `rangeEndIso` means a single day — see the weekly slate in
   *  ValueDashboard/weeklySlate.ts (#712). */
  selectedDateIso: string;
  rangeEndIso?: string;
  /** Day or Week — the SHAPE of that window, which the pair of dates above
   *  cannot state on its own (a week whose games all fall on one day is
   *  indistinguishable from a day). #757's fallback needs it: a weekly board
   *  falls forward a whole week, not a day. */
  slateRange?: SlateRange;
  /** #757/#248 — a focused deep link is pinning the date, so this board stays
   *  exact-date and never falls forward: a stale link must not appear to land
   *  on a neighbouring event. Only the dashboard can see a focus query, so it
   *  travels as a prop; the other half of the question — whether the view is
   *  bounded by a date at all — is already in `dateFilter` above. */
  datePinnedByFocus?: boolean;
  /** Toolbar YES/NO toggle — drives pill highlight and ladder side. */
  marketSide: MarketSide;
  /** Toolbar Maker/Taker/Market toggle — drives the collapsed pill price.
   *  Pills read via `pillPriceCents(book, side, priceMode)` to match the
   *  moneyline GameTable behaviour. */
  priceMode: PriceMode;
  /**
   * Fired with the synthesized GameRow whenever a spread or total row is
   * expanded, or null when it collapses. Parent uses it to render the
   * right-side detail panel.
   */
  onActiveGameChange?: (game: GameRow | null) => void;
  /**
   * Fired alongside `onActiveGameChange` with the bet-description title +
   * chip strip context, so the detail panel can render the alternate
   * header for spread/total bets. Null when no row is expanded.
   */
  onActiveBetLineContextChange?: (context: BetLineContext | null) => void;
  /**
   * Fired when the user clicks an individual Y/N pill — parent seeds the
   * side panel's order ticket (GameDetailPanel tradeIntent).
   */
  onTradeClick?: (data: TradeTicketContext) => void;
  /** #1231 — show the Polymarket US column (the host's sport has US lines). */
  showUsColumn?: boolean;
  /** #1231 — a US pill on a spread/total line: the host opens the shared US
   *  ticket for that exact line (its own path, never onTradeClick/one-click). */
  onUsTradeClick?: (data: TradeTicketContext) => void;
  /**
   * Counter that bumps when the parent's detail panel closes. Tells this
   * view to collapse its inline expansion state so re-opening the panel
   * doesn't require two clicks.
   */
  closeSignal?: number;
  /**
   * #539 — the exact market ticker a focused deep link names, when that link
   * targets a spread or total. This board resolves it rather than the parent
   * because it already holds `getGameEvents` rows memoized, and that selector
   * (unlike `getGames`) is uncached — re-deriving it upstream would rebuild
   * every event on every feed tick.
   *
   * Modelled on `GameDetail`'s `SpreadTotalSection`, which takes the same
   * `focusTicker` / `focusSide` pair — and reuses its exported matcher. The two
   * are NOT identical: this board additionally honours the link's event and
   * venue (below), because it searches MANY events where that section is
   * already scoped to the one it renders.
   */
  focusTicker?: string | null;
  /** The event the link claims. Honoured so a link whose event and ticker
   *  contradict each other resolves to nothing instead of opening the ticker
   *  anyway. */
  focusEventIdentity?: string | null;
  /** The venue the link named. Honoured for the same reason the event is. */
  focusVenue?: 'kalshi' | 'polymarket' | null;
  /** YES/NO side the focused link named. Seeds the ticket's order side. */
  focusSide?: MarketSide;
  /**
   * Reports resolution back to the hydration state machine, which owns the
   * one-shot, the connected-timeout, and the unavailable notice. Fires
   * `searching` until the line arrives (the row may still be in flight, so
   * that is a WAIT, not a verdict) and `opened` once the panel is open.
   */
  onFocusResolution?: (resolution: ExternalFocusResolution) => void;
}

/** #1130 — what the dashboard hands this board when CFB "Saved only" is on. */
export interface SpreadsTotalsSavedOnly {
  /** True when this event is on the trader's saved list (the #120 identity). */
  isSaved: (eventKey: string) => boolean;
  /** Saved games across every sport and date: tells "nothing saved yet" apart
   *  from "none saved here". Not this board's count; that is derived below. */
  totalSaved: number;
  /** Turns Saved only off. The empty state's way back to the full board. */
  onShowFullBoard: () => void;
}

/** Which line on which event a focus ticker names. `idx` is resolved from the
 *  ticker at open time and never stored as the identity — the identity is the
 *  ticker itself, which is what `findSpreadTotalFocusMatch` matches on. */
export interface SpreadsTotalsFocusMatch {
  eventKey: string;
  rowKind: RowKind;
  idx: number;
  /** Which venue's book carried the exact ticker. Carried through so the panel
   *  opens on the venue the link named rather than the board's default. */
  venue: 'kalshi' | 'polymarket';
}

/** Locate a focus ticker across every event row on the board.
 *
 *  `GameDetail` resolves a focus within ONE event it already has; the board has
 *  many, so the event scan is the only genuinely new step — the per-event line
 *  match reuses `findSpreadTotalFocusMatch` verbatim rather than restating it.
 *
 *  `eventIdentity` is REQUIRED to be honoured when the caller supplies it, and
 *  that is not redundant with the ticker. A ticker alone would open any market
 *  it names regardless of which event the link claimed — so a link whose event
 *  and ticker CONTRADICT each other would still open something, and the
 *  ticket's "contradictory identity shows the unavailable state" criterion
 *  would be unmet. Constraining the scan first makes a contradiction resolve to
 *  nothing, which the caller reports as still-searching and the hydration
 *  timeout turns into the notice. (It is the moneyline matcher's rule too —
 *  imported rather than restated, so the two boards cannot disagree.)
 *
 *  Fail-closed by construction otherwise: the reused helper compares exact
 *  market tickers, so a ticker absent from every line returns null (keep
 *  waiting) rather than resolving to a nearby line. Exported for the #539
 *  tests. */
export function findBoardFocusMatch(
  rows: readonly GameEventRow[],
  focusTicker?: string | null,
  eventIdentity?: string | null,
  venue?: 'kalshi' | 'polymarket' | null,
): SpreadsTotalsFocusMatch | null {
  if (!focusTicker) return null;
  for (const row of rows) {
    if (eventIdentity && !focusEventIdentityMatches(eventIdentity, row)) continue;
    const match = findSpreadTotalFocusMatch(row, focusTicker);
    if (!match) continue;
    // The moneyline matcher restricts its search to the requested venue; this
    // path must too, or the two disagree about what a contradictory link means.
    // A ticker belongs to one venue in practice, so this rarely bites — but
    // "rarely" is not the standard for a link that opens an order ticket.
    if (venue && match.venue !== venue) continue;
    return {
      eventKey: row.key,
      rowKind: match.rowKind,
      idx: match.idx,
      venue: match.venue,
    };
  }
  return null;
}

/** When an event sorts — its scheduled start, or midnight on its own date when
 *  the feed hasn't stamped one, and last of all when it has neither. Mirrors
 *  the moneyline board's `gameSortMs`. The last case only ever ORDERS such an
 *  event; `pickUpcomingSlate` is what refuses it, and refuses it on its date
 *  rather than on this number (#765). */
function eventSortMs(event: GameEventRow): number {
  if (event.startTimeMs != null && Number.isFinite(event.startTimeMs)) return event.startTimeMs;
  const dayMs = event.date ? startOfIsoDayMs(event.date) : Number.NaN;
  return Number.isFinite(dayMs) ? dayMs : Number.POSITIVE_INFINITY;
}

export { defaultLineIndex, nearestToFiftyIndex };

export function SpreadsTotals(props: SpreadsTotalsProps) {
  const scope = useAuthStore((s) => spreadTotalLineScope(s.user));
  // Reset all local view state synchronously at an account/org boundary. Only
  // the matching session's remembered identities may seed the next instance.
  return <SpreadsTotalsForSession key={scope ?? 'signed-out'} {...props} selectionScope={scope} />;
}

function SpreadsTotalsForSession({
  sport,
  events: suppliedEvents,
  cfbBucket,
  savedOnly,
  boardLabel,
  titleLabel,
  loading = false,
  statusFilter,
  dateFilter,
  selectedDateIso,
  rangeEndIso,
  slateRange = 'day',
  datePinnedByFocus = false,
  marketSide,
  priceMode,
  onActiveGameChange,
  onActiveBetLineContextChange,
  onTradeClick,
  showUsColumn = false,
  onUsTradeClick,
  closeSignal = 0,
  focusTicker,
  focusEventIdentity,
  focusVenue,
  focusSide,
  onFocusResolution,
  selectionScope,
}: SpreadsTotalsProps & { selectionScope: string | null }) {
  const connected = useMarketStore((s) => s.connected);
  const getGameEvents = useMarketStore((s) => s.getGameEvents);
  const version = useMarketStore((s) => s.version);

  // D-020 LT2-005: under lt2, Poly-only lines are hidden from the execution
  // view unless the reveal toggle is on. Filtered at the SOURCE so idx
  // seeding, focus matching, orderbook needs, and the pill-trade line lookup
  // all see one consistent world. lt2 off → arrays untouched.
  const hidePolyOnlyLines = useHidePolyOnlyLines();
  // Read separately from the rule above: LT2-017's nearest-three depth window
  // asks whether the strip is ON, which is a different question from whether
  // Poly-only lines are hidden.
  const nearestLinesOn = useDisplaySettings((s) => s.spreadTotalNearestLines);

  // Which of the sport's events this board may show, whatever their date.
  // Held apart from the date read so the #757 fallback runs the SAME rule: a
  // bucket or Poly-only filter applied after a date was chosen would land the
  // board on a day whose only rows it then hides.
  const applyBoardFilters = useCallback((fetched: readonly GameEventRow[]): readonly GameEventRow[] => {
    // Filter at the SOURCE, like the Poly-only rule below: line seeding, focus
    // matching, and orderbook subscriptions must all see one world.
    //
    // Both steps hand BACK the same array when they change nothing, so a board
    // whose store read was served from cache keeps one row array across
    // derivations instead of a fresh one each time.
    const base = cfbBucket ? fetched.filter((r) => cfbRowMatchesBucket(r, cfbBucket)) : fetched;
    return executableEvents(base, hidePolyOnlyLines);
  }, [cfbBucket, hidePolyOnlyLines]);

  // May an empty window stand aside for the next one with rows? Both halves of
  // the answer are here: the view has to be bounded by a date at all, and no
  // focused deep link may be pinning it.
  const mayFallForward = dateFilter === 'today' && !datePinnedByFocus;

  // The requested window, and — when a fallback is possible — the whole
  // catalog it would be chosen from, in ONE store read.
  //
  // `getGameEvents` is uncached and re-parses every ticker for the sport on
  // every call, so the bounded read costs nearly as much as the unbounded one
  // (~35ms vs ~42ms on an in-season CFB catalog) and the difference is all in
  // rows we may need anyway. Reading both would double the board's per-flush
  // cost in exactly the state this feature exists for — a fallen-forward board
  // is a board being TRADED, with a full table rendering beside it — so take
  // the wider read once and derive the window from it in memory. The date is
  // the event's own (`row.date`), which is what the store filters on.
  const { requestedRows, fallbackCandidates } = useMemo(() => {
    void version;
    if (suppliedEvents) {
      // Chosen upstream, across sports, over the search's or mode's own window.
      // The CFB bucket is deliberately NOT applied: it narrows the saved board,
      // and a selection the scope line has already counted must not be silently
      // shortened by a preference set for a different board — the same
      // judgement the moneyline results make with the Kalshi-only chip. The
      // execution rule above IS applied, because "may this line be traded
      // here" is not a presentation preference; the dashboard applies it to
      // the same rows before they are counted, so this is a no-op it cannot
      // rely on the caller for.
      // No copy: `executableEvents` hands back the same array when it hides
      // nothing, so a board whose store read was served from cache keeps ONE
      // row array across derivations. Spreading it here would defeat that and
      // re-run every `rows`-keyed effect — line seeding, focus, orderbook
      // needs, the ticket re-synth — on each publication.
      return {
        requestedRows: executableEvents(suppliedEvents, hidePolyOnlyLines),
        fallbackCandidates: null,
      };
    }
    if (!mayFallForward) {
      return {
        requestedRows: applyBoardFilters(
          getGameEvents(sport, { statusFilter, dateFilter, selectedDateIso, rangeEndIso }),
        ),
        fallbackCandidates: null,
      };
    }
    const everyDate = applyBoardFilters(getGameEvents(sport, { statusFilter, dateFilter: 'all' }));
    const window = { startIso: selectedDateIso, endIso: rangeEndIso ?? selectedDateIso };
    return {
      // Filtering preserves the store's order, so the board is row-for-row what
      // the bounded read would have returned.
      requestedRows: everyDate.filter((r) => slateWindowContains(window, r.date)),
      fallbackCandidates: everyDate,
    };
  }, [
    getGameEvents,
    sport,
    applyBoardFilters,
    statusFilter,
    dateFilter,
    selectedDateIso,
    rangeEndIso,
    mayFallForward,
    version,
    suppliedEvents,
    hidePolyOnlyLines,
  ]);

  // #757 — the requested window is empty, so stand in the next one that isn't.
  // Chosen from the SAME store snapshot the board already read (no new
  // subscription, poll or fetch); `version` upstream is what un-sticks an early
  // empty render once markets hydrate.
  const fallbackSlate = useMemo(() => {
    if (!fallbackCandidates || requestedRows.length > 0) return null;
    return pickUpcomingSlate({
      rows: fallbackCandidates.filter((r) => r.gameState !== 'final'),
      fromIso: selectedDateIso,
      range: slateRange,
      dateOf: (r) => r.date,
      sortMs: eventSortMs,
      tieBreak: (r) => r.awayCode,
    });
  }, [fallbackCandidates, requestedRows.length, selectedDateIso, slateRange]);

  // The window's rows, whichever window they came from, before Saved only.
  const windowRows = fallbackSlate?.rows ?? requestedRows;

  // #1130 — Saved only narrows the window AFTER it is chosen, so the board
  // stays on the same date as the Moneyline board and as itself with the
  // filter off; it never falls forward to a day just because a saved game is
  // there. Discovery rows are a selection the scope line already counted, so
  // they are left alone, as the moneyline results leave them.
  const savedFilter = suppliedEvents ? undefined : savedOnly;
  // A focused deep link is explicit intent, and beats the standing filter: the
  // linked event stays on the board so the ticket opens, as it does on
  // Moneyline. Matched against the unfiltered rows for that reason.
  const focusAdmittedKey = useMemo(
    () =>
      savedFilter
        ? findBoardFocusMatch(windowRows, focusTicker, focusEventIdentity, focusVenue)?.eventKey ?? null
        : null,
    [savedFilter, windowRows, focusTicker, focusEventIdentity, focusVenue],
  );

  // The rows ON SCREEN. Everything below — line seeding, focus matching,
  // orderbook needs, the game-state poll, pill trades — reads this one name,
  // so neither the fallback nor the saved filter can reach the table without
  // reaching them too. Hands back `windowRows` itself when nothing is hidden.
  const rows = useMemo(() => {
    if (!savedFilter) return windowRows;
    const kept = windowRows.filter(
      (r) => savedFilter.isSaved(r.key) || r.key === focusAdmittedKey,
    );
    return kept.length === windowRows.length ? windowRows : kept;
  }, [savedFilter, windowRows, focusAdmittedKey]);
  // The title's count: saved games on this board, not a focus-admitted one.
  const savedRowCount = useMemo(
    () => (savedFilter ? rows.filter((r) => savedFilter.isSaved(r.key)).length : undefined),
    [savedFilter, rows],
  );

  // Batched Unabated gameState poll for the visible event rows. Drives
  // the period status text + per-team scores in MatchupRow once a game
  // starts. Pulls from the same hydra endpoint the moneyline page
  // polls; ValueDashboard already polls when the moneyline tab is up,
  // but the spread/total tab needs its own subscription scoped to its
  // events (which can be a subset/superset of the dashboard view).
  const eventGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const k = toHydraGameKeyLive(r.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [rows]);
  useGameStatesPoll(eventGameKeys);

  // #1071 — the highest-volume initial line, behind a switch read when the
  // board mounts (`initialLineConfig.ts`). Signed out there is nowhere to
  // store a default, so the board keeps today's 50¢ seed.
  const [volumeRuleOn] = useState(initialLineVolumeEnabled);
  const volumeRuleActive = volumeRuleOn && selectionScope != null;
  // When each row began showing its 50¢ line while its window volume arrives.
  const initialLineWaitRef = useRef(new Map<string, LocalClockStamp>());
  // Rows this mount seeded or reused — so only a remount (Back) counts as
  // reusing a stored default.
  const initialLineSeenRef = useRef(new Set<string>());
  // Bumped when a wait runs out: settles and seeds again with no new frame.
  // Touches key the row themselves: a chip or working-order pick, an expand,
  // a ticket click (`keyTouchedLine`). Every way this table aims a card (row
  // click, chip, working-order link) is one of those and runs before the
  // hotkey focus moves, so an aim needs no handler of its own. The one
  // exception stores nothing on purpose: a row click on an unpriced row,
  // whose line on show is #780's placeholder; if that row is still expanded
  // or aimed once it has a 50¢ line while its volume is pending, the settle
  // keeps the line it shows.
  const [initialLineTick, setInitialLineTick] = useState(0);
  const seedTick = volumeRuleActive ? initialLineTick : 0;

  // Per-event selection is stored by LINE IDENTITY, never array position.
  // Hydra can reorder a strip as books move; retaining an index would silently
  // retarget the trader's focused card and hotkeys to a different market.
  const [spreadKeyByEvent, setSpreadKeyByEvent] = useState(() => readSpreadTotalLines(selectionScope, 'spread'));
  const [totalKeyByEvent, setTotalKeyByEvent] = useState(() => readSpreadTotalLines(selectionScope, 'total'));
  // #1071 — switched on, a row with no stored key yet (Back, a row returning
  // after a sport or date switch, its first pass) renders what the seed is
  // about to store, so it never paints the 50¢ line for a frame and then jumps.
  const spreadIdxByEvent = useMemo(
    () => resolveLineIndices(spreadKeyByEvent, rows, (r) => r.spreads, spreadLineKey,
      volumeRuleActive ? displayedInitialLine(selectionScope, 'spread', spreadLineKey) : undefined),
    [spreadKeyByEvent, rows, volumeRuleActive, selectionScope],
  );
  const totalIdxByEvent = useMemo(
    () => resolveLineIndices(totalKeyByEvent, rows, (r) => r.totals, totalLineKey,
      volumeRuleActive ? displayedInitialLine(selectionScope, 'total', totalLineKey) : undefined),
    [totalKeyByEvent, rows, volumeRuleActive, selectionScope],
  );
  // Exactly one expansion across all events — the shape, not writer
  // discipline, is what enforces it. See ExpandedRow in SpreadsTotalsTable.
  const [expandedRow, setExpandedRow] = useState<ExpandedRow>(null);
  // #1143 — a placeholder row may be aimed before it has a stored line.
  // Keep only that aim's committed identity; never freeze its visible default.
  const focusedCardId = useLt2FocusStore((s) => s.focusedCardId);
  const unseededAimRef = useRef<{ id: string; key: string; label: string } | null>(null);
  const retiredInitialAimRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const previous = unseededAimRef.current;
    unseededAimRef.current = null;
    if (focusedCardId == null) return;
    for (const row of rows) {
      for (const kind of ['spread', 'total'] as const) {
        const id = lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, row.key, kind);
        if (id !== focusedCardId) continue;
        const idx = (kind === 'spread' ? spreadIdxByEvent : totalIdxByEvent).get(row.key) ?? 0;
        const line = kind === 'spread' ? row.spreads[idx] : row.totals[idx];
        if (!line) return;
        const key = kind === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine);
        if (previous?.id === id && previous.key !== key) {
          // Keep this pass's existing worked-row default decision. Only the
          // executable aim is retired; #1071's display policy is unchanged.
          retiredInitialAimRef.current = id;
          useLt2FocusStore.getState().clearFocusIf(id, { lineChanged: true, label: previous.label });
          return;
        }
        if (!(kind === 'spread' ? spreadKeyByEvent : totalKeyByEvent).has(row.key)) {
          unseededAimRef.current = { id, key, label: useLt2FocusStore.getState().resolveFocused()?.cardLabel ?? 'The aimed line' };
        }
        return;
      }
    }
  }, [focusedCardId, rows, spreadIdxByEvent, totalIdxByEvent, spreadKeyByEvent, totalKeyByEvent]);

  // The parent passes inline arrows, so these change identity every render.
  // Held in refs and kept OUT of the selection effect's deps: including them
  // would re-run it on every parent render, and the focus latch below — not
  // dependency stability — is what makes applying a focus idempotent.
  const onTradeClickRef = useRef(onTradeClick);
  const onFocusResolutionRef = useRef(onFocusResolution);
  useEffect(() => {
    onTradeClickRef.current = onTradeClick;
    onFocusResolutionRef.current = onFocusResolution;
  });

  // #539 one-shot, keyed on the TICKER (plus side), never on the resolved
  // index: the index is re-derived from the ticker each pass and is not the
  // identity. Without the latch a focus would re-open the ticket on every feed
  // tick, stamping over a trader who had since moved to another line.
  const appliedFocusRef = useRef<string | null>(null);

  // #608 — when the selected market leaves the feed the card stays mounted
  // under the same id, so nothing unregisters and the held hotkey focus
  // survives while the card falls back to a default line. Drop the focus for
  // exactly that card. A LAYOUT effect so it lands before the passive seed
  // below overwrites the stale key — after that the mismatch is invisible.
  // The visible fallback selection is untouched: refocusing is the trader's
  // explicit act.
  useLayoutEffect(() => {
    const { clearFocusIf } = useLt2FocusStore.getState();
    for (const row of rows) {
      const spreadKey = spreadKeyByEvent.get(row.key);
      if (spreadKey != null && !row.spreads.some((l) => spreadLineKey(l) === spreadKey)) {
        clearFocusIf(lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, row.key, 'spread'), { marketGone: true });
      }
      const totalKey = totalKeyByEvent.get(row.key);
      if (totalKey != null && !row.totals.some((l) => totalLineKey(l) === totalKey)) {
        clearFocusIf(lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, row.key, 'total'), { marketGone: true });
      }
    }
  }, [rows, spreadKeyByEvent, totalKeyByEvent]);

  // #1071 — settle each row's initial line BEFORE the seed below reads it.
  // Switched on, the rule's final answer goes to the default map, which the
  // seed applies; switched off, it is only counted (shadow) and the seed keeps
  // today's 50¢ default. In the effect body, not a state updater, so the waits
  // it starts are in place for the timer effect after the seed.
  useEffect(() => {
    const ctx: InitialLineContext = {
      scope: selectionScope,
      active: volumeRuleActive,
      waits: initialLineWaitRef.current,
      seen: initialLineSeenRef.current,
      worked: (kind, event) =>
        (expandedRow?.eventKey === event && expandedRow.rowKind === kind)
        || retiredInitialAimRef.current === lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, event, kind)
        || useLt2FocusStore.getState().focusedCardId === lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, event, kind),
    };
    const present = new Set<string>();
    for (const row of rows) {
      present.add(initialLineId('spread', row.key)).add(initialLineId('total', row.key));
      settleInitialLine(ctx, 'spread', row, row.spreads, spreadLineKey);
      settleInitialLine(ctx, 'total', row, row.totals, totalLineKey);
    }
    // A row that leaves mid-wait starts a fresh wait if it returns.
    for (const id of ctx.waits.keys()) if (!present.has(id)) ctx.waits.delete(id);
    retiredInitialAimRef.current = null;
  }, [rows, selectionScope, volumeRuleActive, initialLineTick, expandedRow]);

  // Establishes which line is selected for the rows currently on the board:
  // a closest-to-50¢ default for rows that just arrived, then #539's focused
  // deep link on top. Deliberately ONE effect rather than two — the focus must
  // win over the default, and ordering inside a single effect is guaranteed
  // where ordering between two sibling effects is merely conventional.
  useEffect(() => {
    setSpreadKeyByEvent((prev) =>
      seedLineKeys(prev, rows, (r) => r.spreads, spreadLineKey,
        volumeRuleActive ? storedLineIndex(selectionScope, 'spread', spreadLineKey) : nearestToFiftyIndex,
        (event, line) => rememberedSpreadTotalLine(selectionScope, 'spread', event) === line),
    );
    setTotalKeyByEvent((prev) =>
      seedLineKeys(prev, rows, (r) => r.totals, totalLineKey,
        volumeRuleActive ? storedLineIndex(selectionScope, 'total', totalLineKey) : nearestToFiftyIndex,
        (event, line) => rememberedSpreadTotalLine(selectionScope, 'total', event) === line),
    );
    // Drop the expansion only if its event left the feed. Returning `prev`
    // otherwise keeps the reference stable, avoiding an extra state update →
    // re-render → second effect pass when the expansion didn't change.
    setExpandedRow((prev) =>
      prev && !rows.some((r) => r.key === prev.eventKey) ? null : prev,
    );

    // ── #539: a focused deep link naming a spread or total ────────────
    if (!focusTicker) {
      appliedFocusRef.current = null;
      return;
    }
    const applyKey = `${focusTicker.toUpperCase()}:${focusSide ?? ''}`;
    if (appliedFocusRef.current === applyKey) return;

    const match = findBoardFocusMatch(rows, focusTicker, focusEventIdentity, focusVenue);
    const row = match ? rows.find((r) => r.key === match.eventKey) : undefined;
    const line =
      match && row
        ? match.rowKind === 'spread'
          ? row.spreads[match.idx]
          : row.totals[match.idx]
        : undefined;
    // Not found is NOT a verdict — the event or its lines may still be
    // arriving. The hydration hook's connected-timeout is what eventually turns
    // a persistent `searching` into the unavailable notice, exactly as it
    // treats a moneyline miss.
    if (!match || !row || !line) {
      onFocusResolutionRef.current?.('searching');
      return;
    }

    appliedFocusRef.current = applyKey;
    // Applied AFTER the default seeding above, in the same effect, so the
    // focused line overwrites the default rather than racing it. On an
    // unpriced row the seeding writes nothing (#780) and this is the only
    // writer — the ordering matters just as much there, because the key it
    // leaves is what stops the later re-seed from reclaiming the row.
    if (match.rowKind === 'spread') {
      rememberSpreadTotalLine(selectionScope, 'spread', row.key, spreadLineKey(line as SpreadLine));
      setSpreadKeyByEvent((prev) =>
        new Map(prev).set(row.key, spreadLineKey(line as SpreadLine)),
      );
    } else {
      rememberSpreadTotalLine(selectionScope, 'total', row.key, totalLineKey(line as TotalLine));
      setTotalKeyByEvent((prev) =>
        new Map(prev).set(row.key, totalLineKey(line as TotalLine)),
      );
    }
    setExpandedRow({ eventKey: row.key, rowKind: match.rowKind });

    // Seeds the ticket through the SAME `onTradeClick` contract a pill click
    // uses — no second order-entry path. The synthesized row's away/home slots
    // reference one shared book, so 'away' is the canonical slot, matching the
    // choice GameDetail's focus path makes. No price is passed: the URL layer
    // never carries one and the panel derives the current book price itself.
    onTradeClickRef.current?.({
      game:
        match.rowKind === 'spread'
          ? synthesizeFromSpread(row, line as SpreadLine)
          : synthesizeFromTotal(row, line as TotalLine),
      teamSide: 'away',
      venue: match.venue,
      initialOrderSide: focusSide ?? marketSide,
      postOnly: priceMode === 'maker',
    });
    onFocusResolutionRef.current?.('opened');
  }, [rows, focusTicker, focusEventIdentity, focusVenue, focusSide, marketSide, priceMode, selectionScope,
    volumeRuleActive, seedTick]);

  // #1071 — wake the settle + seed when the earliest running wait runs out,
  // so a row whose window volume never arrives is seeded with no further frame.
  // Every wait left here belongs to a row on the board that the settle above
  // saw still waiting, so one that has run out since is due now, not dropped.
  // Switched off nothing is seeded, so no timer runs and the shadow count
  // never costs a render: the first pass after the limit counts it instead.
  useEffect(() => {
    if (!volumeRuleActive) return;
    let due = Infinity;
    for (const started of initialLineWaitRef.current.values()) {
      due = Math.min(due, INITIAL_LINE_WAIT_MS - elapsedSince(started));
    }
    if (due === Infinity) return;
    // Rounded up: a timer that fires a fraction early would find it waiting.
    const timer = setTimeout(() => setInitialLineTick((n) => n + 1), Math.max(0, Math.ceil(due)));
    return () => clearTimeout(timer);
  }, [rows, selectionScope, volumeRuleActive, initialLineTick]);

  // Orderbook needs — the currently-selected spread AND total ticker per
  // event, even when collapsed. Pills use `pillPriceCents` which derives
  // best ask from the opposite side's bid stream, mirroring the moneyline
  // GameTable behaviour and matching Kalshi's quick-view ask prices. Cost
  // scales with rendered events × 2 strips × 2 venues, not Σ lines.

  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const spread = row.spreads[spreadIdxByEvent.get(row.key) ?? 0];
      const total = row.totals[totalIdxByEvent.get(row.key) ?? 0];
      if (spread?.kalshi?.marketTicker) set.add(spread.kalshi.marketTicker);
      if (spread?.polymarket?.marketTicker) set.add(spread.polymarket.marketTicker);
      if (total?.kalshi?.marketTicker) set.add(total.kalshi.marketTicker);
      if (total?.polymarket?.marketTicker) set.add(total.polymarket.marketTicker);
    }
    // D-020 LT2-017: the expanded row's nearest-three window needs depth for
    // its neighbor lines too. One extra event × ≤2 tickers — bounded.
    if (nearestLinesOn && expandedRow) {
      const row = rows.find((r) => r.key === expandedRow.eventKey);
      if (row) {
        const lines = expandedRow.rowKind === 'spread' ? row.spreads : row.totals;
        const selected =
          expandedRow.rowKind === 'spread'
            ? spreadIdxByEvent.get(row.key) ?? 0
            : totalIdxByEvent.get(row.key) ?? 0;
        for (const idx of venueAwareLineWindow(lines, selected)) {
          const t = lines[idx]?.kalshi?.marketTicker;
          if (t) set.add(t);
        }
      }
    }
    return Array.from(set);
  }, [rows, spreadIdxByEvent, totalIdxByEvent, nearestLinesOn, expandedRow]);

  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('spreads-totals', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('spreads-totals', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Full ladder for the games the trader WORKS (Dan, 2026-10-03): a game they
  // picked a line on this session, or the expanded one, streams every strike
  // from the edge instead of the floor — gbuff he2's alternate CFB strikes
  // went stale or missing while being traded. Per game, never the slate
  // (see setBoardFullLadderTickers). The pick memory is the session's own
  // record of a trader's choices; the selection maps re-run this on a pick.
  const fullLadderTickers = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const worked = expandedRow?.eventKey === row.key
        || (spreadKeyByEvent.has(row.key) && rememberedSpreadTotalLine(selectionScope, 'spread', row.key) != null)
        || (totalKeyByEvent.has(row.key) && rememberedSpreadTotalLine(selectionScope, 'total', row.key) != null);
      if (!worked) continue;
      for (const line of [...row.spreads, ...row.totals]) {
        if (line.kalshi?.marketTicker) set.add(line.kalshi.marketTicker);
        if (line.polymarket?.marketTicker) set.add(line.polymarket.marketTicker);
      }
    }
    return [...set].sort();
  }, [rows, expandedRow, selectionScope, spreadKeyByEvent, totalKeyByEvent]);
  const fullLadderKey = fullLadderTickers.join('|');
  useEffect(() => {
    setBoardFullLadderTickers(VALUE_DASHBOARD_FEED_SOURCE, fullLadderTickers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullLadderKey]);
  useEffect(() => () => setBoardFullLadderTickers(VALUE_DASHBOARD_FEED_SOURCE, []), []);

  // Stable references: select arrays directly; the table walks them for
  // per-event strip aggregation. Don't `.map()` inside the selector —
  // that mints a new array every render and infinite-loops.
  const openOrders = useAccountStore((s) => s.openOrders);
  const accountPositions = useAccountStore((s) => s.positions);

  // Snapshot filter is ONLY the user's own tickers (open orders +
  // positions). PositionTracker does the same — keeps Kalshi's
  // /portfolio/orders/queue_positions call narrow, which is the only
  // way queue depth comes back reliably. Pre-spread/total fix, we sent
  // every visible spread/total ticker to the snapshot (hundreds across
  // sports + events) and Kalshi's response silently dropped queue data
  // for some tickers — chips showed `count/?`. C/O ring + position
  // aggregation reads accountStore directly, so they don't need
  // visible tickers in the snapshot.
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const o of openOrders) if (o.ticker) set.add(o.ticker);
    for (const p of accountPositions) if (p.ticker) set.add(p.ticker);
    return Array.from(set);
  }, [openOrders, accountPositions]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);

  // A row with no key yet is keyed to the line a ticket click touched, at once,
  // as a chip pick does; so is an expand, except on #780's placeholder (see
  // handleToggleExpand), so in practice switched on, on a row waiting on its
  // volume. Switched off (or signed out, where nothing is remembered) the key
  // is what holds the line: an unpriced row can still have a live price button
  // (e.g. Polymarket on a cold Kalshi strip), and without the key its first
  // priced pass seeds another line and the open ticket, which follows the row,
  // changes market under the trader (#823). #608's focus clear and the
  // worked-game ladder need the key in both switch modes (the ladder only
  // signed in, alongside the remembered pick).
  const keyTouchedLine = useCallback((rowKind: RowKind, eventKey: string, key: string) => {
    if (unseededAimRef.current?.id === lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, eventKey, rowKind)) {
      unseededAimRef.current = null; // This ticket pick owns the new line.
    }
    const set = rowKind === 'spread' ? setSpreadKeyByEvent : setTotalKeyByEvent;
    set((prev) => prev.has(eventKey) ? prev : new Map(prev).set(eventKey, key));
  }, []);

  // Chip-strip scrolls implicitly activate the row they're on. Otherwise
  // a trader scrolling chips on row B while row A is still expanded would
  // see the side panel keep rendering A — confusing because the chip
  // they're touching IS visibly highlighting on B. Mirrors the
  // "only one expanded across all events" rule handleToggleExpand
  // enforces: this row's kind becomes the sole expansion.
  const handleSelectSpreadIdx = useCallback((eventKey: string, idx: number) => {
    const line = rows.find((row) => row.key === eventKey)?.spreads[idx];
    if (!line) return;
    if (unseededAimRef.current?.id === lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, eventKey, 'spread')) unseededAimRef.current = null;
    rememberSpreadTotalLine(selectionScope, 'spread', eventKey, spreadLineKey(line));
    setSpreadKeyByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, spreadLineKey(line));
      return next;
    });
    setExpandedRow({ eventKey, rowKind: 'spread' });
  }, [rows, selectionScope]);

  const handleSelectTotalIdx = useCallback((eventKey: string, idx: number) => {
    const line = rows.find((row) => row.key === eventKey)?.totals[idx];
    if (!line) return;
    if (unseededAimRef.current?.id === lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, eventKey, 'total')) unseededAimRef.current = null;
    rememberSpreadTotalLine(selectionScope, 'total', eventKey, totalLineKey(line));
    setTotalKeyByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, totalLineKey(line));
      return next;
    });
    setExpandedRow({ eventKey, rowKind: 'total' });
  }, [rows, selectionScope]);

  const handleToggleExpand = useCallback((eventKey: string, row: RowKind) => {
    const event = rows.find((candidate) => candidate.key === eventKey);
    const lines = row === 'spread' ? event?.spreads : event?.totals;
    const line = lines?.[(row === 'spread' ? spreadIdxByEvent : totalIdxByEvent).get(eventKey) ?? 0];
    // A row with no line keyed and no 50¢ line yet (no priced Kalshi line; on a
    // Polymarket-only strip, no priced line) is showing #780's rendered
    // fallback: never a line to keep, so the expand stores nothing and the row
    // seeds normally once priced, after Back too (#823), switched on or off.
    const placeholder = lines != null && nearestToFiftyIndex(lines) == null
      && !(row === 'spread' ? spreadKeyByEvent : totalKeyByEvent).has(eventKey);
    if (line && !placeholder) {
      const key = row === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine);
      rememberSpreadTotalLine(selectionScope, row, eventKey, key);
      keyTouchedLine(row, eventKey, key);
    }
    // Clicking a spread/total in event B closes whatever was open in event
    // A — the parent detail panel renders a single context. Re-clicking the
    // same row collapses it.
    setExpandedRow((prev) =>
      prev?.eventKey === eventKey && prev.rowKind === row
        ? null
        : { eventKey, rowKind: row },
    );
  }, [rows, spreadIdxByEvent, totalIdxByEvent, selectionScope, spreadKeyByEvent, totalKeyByEvent, keyTouchedLine]);

  // Parent closes its panel → collapse our inline expansion so the next
  // click on the same row re-opens the panel cleanly. Skip the very first
  // run (closeSignal=0 at mount).
  useEffect(() => {
    if (closeSignal === 0) return;
    setExpandedRow(null);
  }, [closeSignal]);


  // Notify parent of the currently-active (event, market-type) → synthesized
  // GameRow so the right-side detail panel can render. Also fires the
  // matching BetLineContext so the panel can render its alternate header
  // (bet-description title + chip strip). Both fire `null` when no row is
  // expanded across any event.
  useEffect(() => {
    let active: GameRow | null = null;
    let context: BetLineContext | null = null;
    // Direct lookup, not a rows-ordered scan: the expanded event IS the one
    // named in state, so the panel can no longer resolve to a different
    // event than the trade (#215).
    const row = expandedRow ? rows.find((r) => r.key === expandedRow.eventKey) : undefined;
    if (row && expandedRow) {
      if (expandedRow.rowKind === 'spread') {
        const idx = spreadIdxByEvent.get(row.key) ?? 0;
        const line = row.spreads[idx];
        if (line) {
          active = synthesizeFromSpread(row, line);
          context = buildBetLineContext({
            kind: 'spread',
            event: row,
            lines: row.spreads,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectSpreadIdx(row.key, next),
          });
        }
      } else {
        const idx = totalIdxByEvent.get(row.key) ?? 0;
        const line = row.totals[idx];
        if (line) {
          active = synthesizeFromTotal(row, line);
          context = buildBetLineContext({
            kind: 'total',
            event: row,
            lines: row.totals,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectTotalIdx(row.key, next),
          });
        }
      }
    }
    onActiveGameChange?.(active);
    onActiveBetLineContextChange?.(context);
  }, [
    rows,
    expandedRow,
    spreadIdxByEvent,
    totalIdxByEvent,
    onActiveGameChange,
    onActiveBetLineContextChange,
    handleSelectSpreadIdx,
    handleSelectTotalIdx,
  ]);

  // The line a pill click names, its row expanded and remembered, as the
  // synthesized GameRow a ticket opens on. Shared by the K/P and US pills so
  // the two can never resolve a different line.
  const resolvePillLine = useCallback(
    (eventKey: string, rowKind: RowKind, lineIdx?: number): GameRow | null => {
      // Deliberately a set, never a toggle-off (unlike handleToggleExpand):
      // a pill click carries a price and side; collapsing on re-click would
      // discard the trade intent.
      setExpandedRow({ eventKey, rowKind });
      const event = rows.find((r) => r.key === eventKey);
      if (!event) return null;
      // A multi-line block click names its OWN line via meta.lineIdx —
      // the selected idx only governs when the click has no line of its own.
      const idx =
        lineIdx ??
        (rowKind === 'spread'
          ? spreadIdxByEvent.get(eventKey) ?? 0
          : totalIdxByEvent.get(eventKey) ?? 0);
      const line = rowKind === 'spread' ? event.spreads[idx] : event.totals[idx];
      if (!line) return null;
      // A trade is a deliberate choice of this market, so it is remembered
      // even on an unpriced row (unlike an expand's placeholder line).
      const lineKey = rowKind === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine);
      rememberSpreadTotalLine(selectionScope, rowKind, eventKey, lineKey);
      keyTouchedLine(rowKind, eventKey, lineKey);
      // Follow the click: a neighbor-line trade moves the selection so the
      // panel/chips agree with the market that was just clicked.
      if (lineIdx != null) {
        if (rowKind === 'spread') handleSelectSpreadIdx(eventKey, lineIdx);
        else handleSelectTotalIdx(eventKey, lineIdx);
      }
      return rowKind === 'spread'
        ? synthesizeFromSpread(event, line as SpreadLine)
        : synthesizeFromTotal(event, line as TotalLine);
    },
    [
      rows,
      spreadIdxByEvent,
      totalIdxByEvent,
      handleSelectSpreadIdx,
      handleSelectTotalIdx,
      selectionScope,
      keyTouchedLine,
    ],
  );

  const handlePillTrade = useCallback(
    (
      eventKey: string,
      rowKind: RowKind,
      venue: 'kalshi' | 'polymarket',
      side: 'yes' | 'no',
      cents: number,
      meta?: PillTradeMeta,
    ) => {
      if (!onTradeClick) {
        setExpandedRow({ eventKey, rowKind });
        return;
      }
      const game = resolvePillLine(eventKey, rowKind, meta?.lineIdx);
      if (!game) return;
      onTradeClick({
        game,
        teamSide: 'away', // synthetic GameRow has identical away/home books
        venue,
        prefillPrice: cents,
        initialOrderSide: side,
        postOnly: false,
        // Click provenance for the LT2 one-click interception layer only —
        // the ticket ignores it (see TradeTicketContext.meta).
        meta,
      });
    },
    [onTradeClick, resolvePillLine],
  );

  // #1231 — a US pill: the same line, handed to the host's US path.
  const handleUsPillTrade = useCallback(
    (eventKey: string, rowKind: RowKind, side: 'yes' | 'no', cents: number) => {
      const game = resolvePillLine(eventKey, rowKind);
      if (!game || !onUsTradeClick) return;
      onUsTradeClick({ game, teamSide: 'away', venue: 'polymarket', prefillPrice: cents, initialOrderSide: side, postOnly: false });
    },
    [onUsTradeClick, resolvePillLine],
  );

  // Named the way the fallback banner and the table title name the board: a
  // bucketed board that says "CFB · FBS" with rows must not call itself plain
  // "CFB" the moment it has none.
  // The banner that says the board moved to a later window. Shared with the
  // Saved-only empty state, which must not name a date the board is not on.
  const fallbackBanner = fallbackSlate && (
    // Above the table, not below it: a reader who met the rows first would
    // have no reason to doubt they are the window the toolbar names.
    <div
      className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs"
    >
      No {boardLabel ?? sport.toUpperCase()} spreads or totals{' '}
      {slateScopeCopy({ startIso: selectedDateIso, endIso: rangeEndIso ?? selectedDateIso }, slateRange)}.{' '}
      {upcomingSlateCopy(fallbackSlate.window, slateRange, 'lines')}
    </div>
  );

  // #1130 — this window HAS lines and Saved only hid every one. Never a silent
  // blank: say which control emptied the board and offer the way back.
  if (savedFilter && rows.length === 0 && windowRows.length > 0 && !loading) {
    const shownWindow = fallbackSlate?.window
      ?? { startIso: selectedDateIso, endIso: rangeEndIso ?? selectedDateIso };
    return (
      <>
        {fallbackBanner}
        <div className="border-border/60 bg-card/40 text-muted-foreground mb-3 rounded-lg border px-4 py-2 text-xs">
          {savedFilter.totalSaved === 0
            ? 'Saved only is on and you haven’t saved any games yet.'
            : `Saved only is on, and none of your saved games has a ${boardLabel ?? sport.toUpperCase()} spread or total ${slateScopeCopy(shownWindow, slateRange)}.`}{' '}
          <button
            type="button"
            onClick={savedFilter.onShowFullBoard}
            className="text-primary hover:text-primary/80 rounded font-semibold underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
          >
            Show the full board
          </button>
        </div>
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
        {connected && loading ? (
          <>
            <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
            <span>Loading {boardLabel ?? sport.toUpperCase()} spreads and totals…</span>
          </>
        ) : connected ? (
          <span>No {boardLabel ?? sport.toUpperCase()} spreads or totals</span>
        ) : (
          <>
            <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
            <span>Connecting to Hydra...</span>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {fallbackBanner}
      <SpreadsTotalsTable
        rows={rows}
        savedCount={savedRowCount}
        boardLabel={boardLabel}
        titleLabel={titleLabel}
        marketSide={marketSide}
        priceMode={priceMode}
        spreadIdxByEvent={spreadIdxByEvent}
        totalIdxByEvent={totalIdxByEvent}
        expandedRow={expandedRow}
        positionByTicker={userActivity.positionByTicker}
        ordersByTicker={userActivity.displayOrdersByTicker ?? userActivity.ordersByTicker}
        openOrders={openOrders}
        onSelectSpreadIdx={handleSelectSpreadIdx}
        onSelectTotalIdx={handleSelectTotalIdx}
        onToggleExpand={handleToggleExpand}
        onPillTrade={handlePillTrade}
        showUsColumn={showUsColumn}
        onUsPillTrade={onUsTradeClick ? handleUsPillTrade : undefined}
      />
    </>
  );
}

// ── State helpers ─────────────────────────────────────────────────────

/** #780 — this map holds SETTLED selections only: a default chosen against
 *  real prices, or a line the trader (or a #539 focus) picked. A row whose
 *  books carry no price yet has no midline to record, so it is left OUT and
 *  `resolveLineIndices` renders the index-0 fallback for it. The next pass
 *  that does carry prices then seeds it for the first time, on the midline.
 *
 *  Storing the fallback instead is the bug: `defaultFn` had no information,
 *  the ladder end got written down as though it were a real answer, and the
 *  `existing != null` branch below kept it there for the life of the row.
 *  Deriving-until-priced is also what the two sibling surfaces already do —
 *  GameDetail's `SpreadTotalSection` recomputes its default in a memo over
 *  `event.spreads` (switched off; with #1071's switch on it settles once), and `GameLinesSection`'s `rungKeyByGroup` is written only
 *  by an explicit pick (a rung click or a focus link), never seeded with a
 *  computed default. Its own comment states the rule this restores: "Absent —
 *  or a stored rung no longer listed — falls back to the closest-to-50¢
 *  default." */
function seedLineKeys<T>(
  prev: Map<string, string>,
  rows: readonly GameEventRow[],
  pick: (r: GameEventRow) => T[],
  keyFor: (line: T) => string,
  defaultFn: (lines: T[], row: GameEventRow) => number | null,
  retain: (event: string, line: string) => boolean,
): Map<string, string> {
  let changed = false;
  // Keep explicit picks while their event is outside the current sport/date/
  // filter. Defaults still disappear with the view. Visible lines below must
  // revalidate by identity, including before any restored ticker is actionable.
  const next = new Map([...prev].filter(([event, line]) => retain(event, line)));
  for (const row of rows) {
    const lines = pick(row);
    const existing = prev.get(row.key);
    if (existing != null && lines.some((line) => keyFor(line) === existing)) {
      next.set(row.key, existing);
      continue;
    }
    const idx = defaultFn(lines, row);
    const line = idx == null ? undefined : lines[idx];
    const defaultKey = line ? keyFor(line) : undefined;
    next.delete(row.key);
    if (defaultKey) next.set(row.key, defaultKey);
    // Both undefined — an unpriced row that stayed unseeded — is NOT a change,
    // which is what keeps the reference stable and the effect from re-firing.
    if (existing !== defaultKey) changed = true;
  }
  if (!changed && prev.size === next.size) return prev;
  return next;
}

/** #1071 — switched on, a row is seeded only from what was stored for it: the
 *  remembered pick (a touch came first), else the default map the rule wrote.
 *  Neither listed ⇒ null: the row shows its 50¢ line unstored (#780's
 *  rendered fallback) until `settleInitialLine` stores an answer. */
function storedLineIndex<T>(scope: string | null, kind: RowKind, keyFor: (line: T) => string) {
  return (lines: T[], row: GameEventRow): number | null =>
    storedSpreadTotalLineIndex(scope, kind, row.key, lines, keyFor);
}

interface InitialLineContext {
  scope: string | null;
  /** The switch is on: store answers. Off: count them only (shadow). */
  active: boolean;
  /** Row → when it began showing its 50¢ line, waiting on window volume. */
  waits: Map<string, LocalClockStamp>;
  /** Rows this mount seeded or reused. */
  seen: Set<string>;
  /** The row is expanded or holds the hotkey focus right now. */
  worked: (kind: RowKind, event: string) => boolean;
}

const initialLineId = (kind: RowKind, event: string) => JSON.stringify([kind, event]);

/** #1071 — switched on, what a row with no stored key renders: its stored
 *  line, else the rule's answer when it is already final, else the 50¢ line
 *  (`resolveLineIndices`'s fallback). The same answer the settle and seed
 *  are about to store, so storing it changes nothing on screen. */
function displayedInitialLine<T extends SpreadLine | TotalLine>(
  scope: string | null, kind: RowKind, keyFor: (line: T) => string,
) {
  const stored = storedLineIndex(scope, kind, keyFor);
  return (lines: T[], row: GameEventRow): number | null => {
    const idx = stored(lines, row);
    if (idx != null) return idx;
    const step = initialLineStep(lines, nearestToFiftyIndex(lines), null, INITIAL_LINE_WAIT_MS);
    return step.kind === 'seed' ? step.idx : null;
  };
}

function listedIndex<T>(lines: T[], keyFor: (line: T) => string, key: string | undefined): number {
  return key == null ? -1 : lines.findIndex((line) => keyFor(line) === key);
}

/** Whether a stored line already decides this row: a remembered pick, or
 *  (switched on) a stored default, or (switched off) an answer already counted. */
function initialLineSettled<T>(
  ctx: InitialLineContext, kind: RowKind, row: GameEventRow, lines: T[], keyFor: (line: T) => string,
): 'picked' | 'stored' | false {
  if (!ctx.active && initialLineDecided(ctx.scope, kind, row.key)) return 'stored';
  if (listedIndex(lines, keyFor, rememberedSpreadTotalLine(ctx.scope, kind, row.key)) >= 0) return 'picked';
  if (ctx.active && listedIndex(lines, keyFor, spreadTotalDefault(ctx.scope, kind, row.key)) >= 0) return 'stored';
  return false;
}

/** #1071 — one row's initial line, each pass: counts its outcome once and,
 *  switched on, stores the answer in the default map. See the board's effect. */
function settleInitialLine<T extends SpreadLine | TotalLine>(
  ctx: InitialLineContext, kind: RowKind, row: GameEventRow, lines: T[], keyFor: (line: T) => string,
): void {
  const { scope, active } = ctx;
  if (!scope) return;
  const id = initialLineId(kind, row.key);
  const mode = active ? 'active' : 'shadow';
  const settled = initialLineSettled(ctx, kind, row, lines, keyFor);
  if (settled === 'picked') {
    // A pick, expand, ticket click or focus link chose the line first; the
    // rule never decides this row (a no-op once the rule has been counted).
    ctx.waits.delete(id);
    recordInitialLine(scope, kind, row.key, mode, { kind: 'touched' });
    return;
  }
  if (settled === 'stored') {
    ctx.waits.delete(id);
    if (!active) return;
    // Re-stored so a default on the board is never the one the map's bound
    // drops, and counted once per mount as reused (Back, a tab switch).
    rememberSpreadTotalDefault(scope, kind, row.key, spreadTotalDefault(scope, kind, row.key)!);
    if (!ctx.seen.has(id)) {
      ctx.seen.add(id);
      noteInitialLineReused();
    }
    return;
  }
  const started = ctx.waits.get(id);
  const anchor = nearestToFiftyIndex(lines);
  const waited = started ? elapsedSince(started) : null;
  // Switched off no timer wakes the board at the limit, so the first pass
  // after it stands in: the switched-on board would have seeded the 50¢ line
  // then, on the evidence the previous pass saw, whatever this pass brings.
  const step: InitialLineStep = !active && waited != null && waited >= INITIAL_LINE_WAIT_MS && anchor != null
    ? { kind: 'seed', idx: anchor, outcome: { kind: 'limit' } }
    : initialLineStep(lines, anchor, waited, INITIAL_LINE_WAIT_MS);
  if (step.kind === 'wait' && anchor != null && ctx.worked(kind, row.key)) {
    // Being worked but holding no line of its own, and waiting on volume:
    // it keeps the 50¢ line on show rather than move when the answer lands.
    // Two ways here: expanded or aimed after a click on an unpriced row
    // (that expand kept nothing, #780's placeholder). #1143 retires an aim
    // if the placeholder strike changes, while keeping this display decision;
    // or still expanded after its picked or stored
    // line was delisted (#608 has already dropped the hotkey focus), so the
    // open card does not change strike.
    ctx.waits.delete(id);
    ctx.seen.add(id);
    recordInitialLine(scope, kind, row.key, mode, { kind: 'touched' });
    if (active) rememberSpreadTotalDefault(scope, kind, row.key, keyFor(lines[anchor]));
    return;
  }
  if (step.kind === 'wait') {
    if (!started) ctx.waits.set(id, localClockStamp());
    return;
  }
  ctx.waits.delete(id);
  // No 50¢ line (any more): unseeded, and no clock until there is one (#780).
  if (step.kind === 'unanchored') return;
  ctx.seen.add(id);
  const role = step.outcome.kind === 'limit'
    ? boardFeedOwnerRole(row.sport, lines.some((line) => line.kalshi != null) ? 'kalshi' : 'polymarket') ?? 'unknown'
    : undefined;
  recordInitialLine(scope, kind, row.key, mode, step.outcome, role);
  if (active) rememberSpreadTotalDefault(scope, kind, row.key, keyFor(lines[step.idx]));
}

function resolveLineIndices<T>(
  selectedKeys: Map<string, string>,
  rows: readonly GameEventRow[],
  pick: (r: GameEventRow) => T[],
  keyFor: (line: T) => string,
  fallback?: (lines: T[], row: GameEventRow) => number | null,
): Map<string, number> {
  const indices = new Map<string, number>();
  for (const row of rows) {
    const lines = pick(row);
    const selectedKey = selectedKeys.get(row.key);
    const selectedIdx = selectedKey == null
      ? -1
      : lines.findIndex((line) => keyFor(line) === selectedKey);
    indices.set(
      row.key,
      selectedIdx >= 0
        ? selectedIdx
        : fallback?.(lines, row) ?? defaultLineIndex(lines as Array<SpreadLine | TotalLine>),
    );
  }
  return indices;
}

// Re-export shape types for tests / future detail-page reuse.
export type { GameEventRow, SpreadLine, TotalLine };
export type { ExpandedRow, RowKind };
